import React, { useState, useEffect, useRef } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Table, Button, Input, Popconfirm, Card, Modal, message, Pagination } from "antd";
import { EditOutlined, DeleteOutlined, CheckOutlined, SearchOutlined } from "@ant-design/icons";
import { List_Gender_From_Role, Add_Update_Gender_Master, Get_Gender_Users_Count } from "../../../../services/userAPI"; // Adjust path
import { useLocation } from "react-router-dom";
import DeleteIcon from "../../../../Images/deleteIcon.png";
import "./genderConfiguration.css";
import AddPlusIcon from "../../../../Images/addplusIconWhite.png";
import Delete from "../../../../Images/deleteIcon.png";


const GenderConfiguration = ({ roleId: propRoleId, accountId: propAccountId }) => {
    const [searchTerm, setSearchTerm] = useState(""); // For search
    const [currentPage, setCurrentPage] = useState(1); // Pagination state
    const [pageSize, setPageSize] = useState(5); // Set page size to 5
    const [editingKey, setEditingKey] = useState(null); // For handling row edit
    const [editedName, setEditedName] = useState(""); // For storing edited name
    const [isModalVisible, setIsModalVisible] = useState(false); // Modal visibility
    const [newGenderName, setNewGenderName] = useState("");
    const [newGenderCode, setNewGenderCode] = useState(""); // For storing gender code input
    const [deleteId, setDeleteId] = useState(null);
    const nameInputRef = useRef(null); // Separate ref for gender name input
    const codeInputRef = useRef(null);
    const location = useLocation();
    const roleId = propRoleId || location.state?.roleId;
    const accountId = propAccountId || location.state?.accountId;

    const [genderEntries, setGenderEntries] = useState([
        { name: "", code: "" }
    ]);

    const handleGenderEntryChange = (index, field, value) => {
        const updatedEntries = [...genderEntries];
        updatedEntries[index][field] = value;
        setGenderEntries(updatedEntries);
    };

    const handleAddNewEntry = () => {
        if (genderEntries.length < 10) {
            setGenderEntries([...genderEntries, { name: "", code: "" }]);
        } else {
            message.info("Maximum of 10 entries allowed.");
        }
    };

    // Fetch the gender data using the GraphQL query
    const { loading, error, data, refetch } = useQuery(List_Gender_From_Role, {
        variables: { jobroleId: roleId },
    });

    // State for storing the fetched data
    const [fetchedData, setFetchedData] = useState([]);

    // Store the fetched gender data in state
    useEffect(() => {
        if (data) {
            setFetchedData(data.listGenderFromRole || []);
        }
    }, [data]);

    // Filter data based on the search term
    const filteredData = fetchedData.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Paginate the filtered data
    const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page when search term changes
    };

    const handleNextPage = () => {
        setCurrentPage((prev) => prev + 1);
    };

    const handlePreviousPage = () => {
        setCurrentPage((prev) => prev - 1);
    };

    const handleEdit = (record) => {
        setEditingKey(record.key);
        setEditedName(record.name); // Set the name of the gender to edit
    };

    const handleSave = (key) => {
        const updatedData = paginatedData.map((item) =>
            item.key === key ? { ...item, name: editedName } : item
        );
        setEditingKey(null); // Reset the editing state
    };

    const showModal = () => {
        setIsModalVisible(true); // Show the modal
    };

    const handleCancel = () => {
        setIsModalVisible(false); // Close the modal
        setNewGenderName(""); // Optional - not used in UI anymore
        setNewGenderCode(""); // Optional - not used in UI anymore
        setGenderEntries([{ name: "", code: "" }]); // 🧼 Reset input fields
    };


    const [addUpdateGender, { loading: addLoading }] = useMutation(Add_Update_Gender_Master);
    const [checkGenderCount] = useLazyQuery(Get_Gender_Users_Count, { fetchPolicy: "network-only" });

    const handleAddGender = async () => {
        const validEntries = genderEntries.filter(entry => entry.name.trim());

        if (validEntries.length === 0) {
            message.error("Please enter gender name.");
            return;
        }

        try {
            for (const entry of validEntries) {
                const response = await addUpdateGender({
                    variables: {
                        genderName: entry.name,
                        genderCode: entry.code,
                        jobRoleId: roleId,
                        id: null,
                    },
                });

                if (!response.data.addUpdateGenderMaster.success) {
                    message.error("Failed: " + response.data.addUpdateGenderMaster.message);
                    return;
                }
            }

            message.success("All genders added successfully!");
            setIsModalVisible(false);
            setGenderEntries([{ name: "", code: "" }]);
            refetch();
        } catch (error) {
            console.error("Error adding/updating gender:", error);
            message.error("Failed to add/update gender.");
        }
    };

    const handleDelete = async (id, name, code, roleId) => {
        setDeleteId(id);
        try {
            const countResponse = await checkGenderCount({
                variables: {
                    accountId: Number(accountId),
                    jobroleId: Number(roleId),
                    genderId: id,
                },
            });
            const count = countResponse?.data?.getGenderUsersCount ?? 0;
            if (count > 0) {
                message.error(`Cannot delete: ${count} user(s) are assigned to this gender.`);
                setDeleteId(null);
                return;
            }

            const response = await addUpdateGender({
                variables: {
                    genderId: id,
                    genderName: name,
                    genderCode: code,
                    jobRoleId: roleId,
                    status: 0, // Soft delete by setting status to 0
                },
            });

            if (response.data.addUpdateGenderMaster.success) {
                message.success("Gender deleted successfully!");
                refetch(); // Refresh the data
            } else {
                message.error(response.data.addUpdateGenderMaster.message);
            }
        } catch (error) {
            console.error("Error deleting gender:", error);
            message.error("Failed to delete gender.");
        } finally {
            setDeleteId(null);
        }
    };


    const columns = [
        {
            // title: "Gender",
            dataIndex: "name",
            key: "gender",
            render: (text, record) => (
                editingKey === record.key ? (
                    <Input
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onPressEnter={() => handleSave(record.key)}
                    />
                ) : (
                    text
                )
            ),
        },
        {
            // title: "Actions",
            key: "actions",
            render: (_, record) => (
                <div style={{
                    display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%", marginRight: "458px",
                    margintop: "168px"
                }}>
                    {editingKey === record.key ? (
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={() => handleSave(record.key)}
                            style={{ marginRight: 8 }}
                        />
                    ) : (
                        <>
                            <Popconfirm
                                title="Are you sure you want to delete this?"
                                placement="leftTop"
                                overlayClassName="custom-delete-popconfirm"
                                onConfirm={() =>
                                    handleDelete(
                                        record.id,
                                        record.name,
                                        record.code,
                                        record.roleId
                                    )
                                }
                                okText={deleteId === record.id ? " " : "Yes"}
                                cancelText="No"
                                okButtonProps={{
                                    loading: deleteId === record.id,
                                    style: {
                                        backgroundColor: "#AD0101",
                                        color: "#fff",
                                        width: "80px",
                                        height: "40px",
                                        border: "none",
                                        borderRadius: "10px",
                                        marginRight: "35px",
                                    },
                                }}
                                cancelButtonProps={{
                                    style: {
                                        backgroundColor: "#fff",
                                        color: "black",
                                        width: "80px",
                                        height: "40px",
                                        border: "1px solid black",
                                        borderRadius: "10px",
                                    },
                                }}
                            >
                                <img
                                    src={DeleteIcon}
                                    alt="Delete"
                                    style={{ cursor: "pointer", width: "18px", height: "18px" }}
                                />
                            </Popconfirm>
                        </>
                    )}
                </div>
            ),
            align: "right",
        },
    ];

    useEffect(() => {
        if (isModalVisible && nameInputRef.current) {
            setTimeout(() => {
                nameInputRef.current.focus(); // Focus only on the name input
            }, 100);
        }
    }, [isModalVisible]);

    useEffect(() => {
        if (data) {
            const sorted = [...(data.listGenderFromRole || [])].sort((a, b) => {
                // Example: sort by createdAt descending, or ID if you don't have createdAt
                return b.id - a.id;
            });
            setFetchedData(sorted);
        }
    }, [data]);

    const handleDeleteGenderEntry = (indexToDelete) => {
        setGenderEntries((prevEntries) => {
            if (prevEntries.length <= 1) {
                return prevEntries;
            }

            return prevEntries.filter(
                (_, index) => index !== indexToDelete
            );
        });
    };


    return (
        <div>
            <Card className="search-card">
                <label style={{
                    fontFamily: "Inter", fontSize: "20px", fontSize: "24px",
                    fontWeight: 500,
                    lineHeight: "100%",
                    letterSpacing: "-1%",
                    marginBottom: "2%",
                    marginTop: "1%",
                }}>Gender</label>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', borderBottom: 'none', marginTop: '1%' }}>
                    <div className="search-group" style={{ flex: 1 }}>
                        <div className="search-input-wrapper">
                            <Input
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder="Search gender"
                                className="search-input-masters-configuration"
                                suffix={<SearchOutlined />}
                            />
                        </div>
                    </div>

                    <div>
                        <button className="add-gender-btn" onClick={showModal} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* <img src={AddPlusIcon} alt="Add" style={{ marginRight: "7px", height: "15px", width: "16px" }} /> */}
                            Add Gender
                        </button>
                    </div>
                </div>
                <br />
                <Table
                    columns={columns}
                    dataSource={paginatedData}
                    pagination={false}
                    loading={loading}
                    showHeader={false}
                />
                {filteredData.length > pageSize && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={filteredData.length}
                            onChange={(page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            }}
                            // showSizeChanger
                            // pageSizeOptions={['5', '10', '20', '50']}
                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                        />
                    </div>
                )}
            </Card>

            <Modal
                title="Add New Gender"
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <div>
                    {genderEntries.map((entry, index) => (
                        <div
                            key={index}
                            className="relative w-full mb-[10px]"
                        >
                            <Input
                                ref={index === 0 ? nameInputRef : null}
                                value={entry.name}
                                onChange={(e) =>
                                    handleGenderEntryChange(
                                        index,
                                        "name",
                                        e.target.value
                                    )
                                }
                                placeholder="Enter gender name"
                                autoFocus={true}
                                className="gender-name-input w-full"
                            />

                            {/* Show delete icon only when more than one input exists */}
                            {genderEntries.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleDeleteGenderEntry(index)
                                    }
                                    className="
                    absolute
                    right-3
                    top-1/2
                    -translate-y-1/2
                    flex
                    items-center
                    justify-center
                    bg-transparent
                    border-none
                    cursor-pointer
                    p-0
                    z-10
                "
                                >
                                    <img
                                        src={DeleteIcon}
                                        alt="Delete"
                                        className="w-[20px] h-[20px] object-contain"
                                    />
                                </button>
                            )}
                        </div>
                    ))}

                    {genderEntries.length >= 10 && (
                        <div style={{ color: 'red', textAlign: 'center', marginTop: '10px' }}>
                            Not Available
                        </div>
                    )}

                    <div style={{
                        marginTop: "20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}>
                        {/* Left side: Add New */}


                        <button
                            type="button"
                            onClick={handleAddNewEntry}
                            className="gender-add-new-btn"
                            disabled={genderEntries.length >= 10}
                        >
                            {genderEntries.length < 10 ? "Add New" : "Not Available"}
                        </button>



                        {/* Right side: Cancel and Save */}
                        <div>
                            <button
                                className="gender-configuration-cancel-button"
                                onClick={handleCancel}
                                style={{ marginRight: 8 }}
                            >
                                Cancel
                            </button>
                            <button
                                className="masters-configuration-add-button"
                                type="primary"
                                onClick={handleAddGender}
                                disabled={addLoading}
                                style={{ opacity: addLoading ? 0.7 : 1, cursor: addLoading ? 'not-allowed' : 'pointer' }}
                            >
                                {addLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
                            </button>
                        </div>
                    </div>

                </div>
            </Modal>

        </div>
    );
};

export default GenderConfiguration;
